import { Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  collectionGroup,
  deleteDoc,
  doc,
  docData,
  Firestore,
  query,
  setDoc,
  Timestamp,
  where,
  writeBatch,
  limit,
  getDocs,
} from '@angular/fire/firestore';
import {
  Booking,
  BookingChange,
  ChangeEventType,
  GuideDetailInterface,
  newGuideInterface,
} from '../../core/models';
import { catchError, firstValueFrom, Observable, of } from 'rxjs';
import {
  getCalendarWeek,
  utcCalendarDay,
} from '../../core/booking-from.utils';
import { AuthService } from '../auth/auth.service';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { startOfWeek } from 'date-fns';
import { TimestampRange } from '../../core/archive-search.types';
@Injectable({
  providedIn: 'root',
})
export class BookingService {
  constructor(
    private firestore: Firestore,
    private authService: AuthService,
  ) {}

  // Utility: Generate Firestore Path
  private getBookingPath(
    booking: Omit<Booking, 'id'>,
    bookingId?: string,
  ): string {
    const calendarWeek = getCalendarWeek(
      new Date(booking.duration.date.seconds * 1000),
    );
    // For existing bookings, include the bookingId (document reference)
    return `bookings/${calendarWeek}/ships/${booking.ship.id}/sub-bookings`;
  }

  // Create a new booking
  createNewBooking(booking: Booking): Promise<void> {
    const newDocRef = doc(
      collection(this.firestore, this.getBookingPath(booking)),
    );

    booking.id = newDocRef.id;
    booking.hash = uuidv4();

    return setDoc(newDocRef, booking);
  }

  //Save a bach of bookings for import
  async createBookingsBatch(bookings: Booking[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    bookings.forEach((booking) => {
      const path = this.getBookingPath(booking);
      const newDocRef = doc(collection(this.firestore, path));

      booking.id = newDocRef.id;
      booking.hash = uuidv4();
      batch.set(newDocRef, { ...booking });
    });

    await batch.commit();
  }

  // Get bookings for a specific calendar week and ship ID
  getAllBookingsForCW(calendarWeekDate: string | Date): Observable<Booking[]> {
    // Booking days are wall-clock-in-UTC, so the week runs from Monday 00:00Z to the next Monday 00:00Z,
    // whatever the browser's timezone is.
    const monday = startOfWeek(utcCalendarDay(new Date(calendarWeekDate)), {
      weekStartsOn: 1,
    });
    const start = Date.UTC(
      monday.getFullYear(),
      monday.getMonth(),
      monday.getDate(),
    );

    // Directly reference the correct sub-collection path
    const bookingsCollection = collectionGroup(this.firestore, `sub-bookings`);

    const bookingsquery = query(
      bookingsCollection,
      where('duration.date', '>=', Timestamp.fromMillis(start)),
      where(
        'duration.date',
        '<',
        Timestamp.fromMillis(start + 7 * 24 * 60 * 60 * 1000),
      ),
    );
    // Fetch all documents inside the collection without additional filtering
    return collectionData(bookingsquery, { idField: 'id' }) as Observable<
      Booking[]
    >;
  }

  // Get bookings for a specific calendar week and ship ID
  getBookings(
    calendarWeekDate: string | Date,
    shipId: string,
  ): Observable<Booking[]> {
    const calendarWeek = getCalendarWeek(calendarWeekDate); // Get calendar week from the provided date
    // Directly reference the correct sub-collection path
    const bookingsCollection = collection(
      this.firestore,
      `bookings/${calendarWeek}/ships/${shipId}/sub-bookings`,
    );

    // Fetch all documents inside the collection without additional filtering
    return collectionData(bookingsCollection, { idField: 'id' }) as Observable<
      Booking[]
    >;
  }

  // Read a single booking

  getBookingData(
    calendarWeekDate: string | Date,
    shipId: string,
    bookingId: string,
  ): Observable<Booking | null> {
    let calendarWeek = '';
    const calendarWeekRegex = /^\d{4}W\d{2}$/;
    if (calendarWeekDate instanceof Date) {
      calendarWeek = getCalendarWeek(calendarWeekDate);
    } else if (calendarWeekRegex.test(calendarWeekDate)) {
      // It's in the correct format (e.g., 2025W05)
      calendarWeek = calendarWeekDate;
    } else {
      // Handle invalid format
      console.log('calendarWeekDate is not in the correct format');
    }

    const bookingDocRef = doc(
      this.firestore,
      `bookings/${calendarWeek}/ships/${shipId}/sub-bookings/${bookingId}`,
    );
    return docData(bookingDocRef, { idField: 'id' }).pipe(
      catchError(() => of(null)),
    ) as Observable<Booking | null>;
  }

  updateBooking(booking: Booking): Promise<void> {
    if (!booking.id) {
      return Promise.reject(new Error('Booking ID is required for update'));
    }

    const calendarWeek = getCalendarWeek(
      new Date(booking.duration.date.seconds * 1000),
    );

    const bookingDocRef = doc(
      this.firestore,
      `bookings/${calendarWeek}/ships/${booking.ship.id}/sub-bookings/${booking.id}`,
    );

    const currentUser = this.authService.currentUserSig();

    if (currentUser) {
      //  console.log('GotUser For Udpate', currentUser);
      //booking.userLastEditorName = currentUser?.username;
      //booking.userLastEditorId = currentUser?.id;
      //booking.lastEditDate = Timestamp.fromDate(new Date());
    }
    //   console.log('updating', booking);
    return setDoc(bookingDocRef, booking, { merge: true });
  }

  async updateBookingChanges(newBooking: Booking): Promise<void> {
    if (!newBooking.id) {
      return Promise.reject(new Error('Booking ID is required for update'));
    }
    const calendarWeek = getCalendarWeek(
      new Date(newBooking.duration.date.seconds * 1000),
    );

    const bookingDocRef = doc(
      this.firestore,
      `bookings/${calendarWeek}/ships/${newBooking.ship.id}/sub-bookings/${newBooking.id}`,
    );

    //    console.log('BookingDocRef', newBooking.id);
    // Fetch the current booking from the database
    const oldBooking = await firstValueFrom(
      this.getBookingData(calendarWeek, newBooking.ship.id, newBooking.id),
    );

    let changes: Partial<Booking> | null = null;
    if (!oldBooking) {
      //      console.warn('Old booking not found, treating as new entry.');
      if (!newBooking.hash) {
        //      console.log('new hash GEN!', newBooking.hash);
        // Generate a new hash for new entries
        newBooking.hash = uuidv4();
      }
    } else {
      changes = this.detectChanges(oldBooking, newBooking);
      if (changes) {
        const currentUser = this.authService.currentUserSig();
        if (currentUser) {
          const changeEvent: BookingChange = {
            timestamp: Timestamp.fromDate(new Date()),
            changeAuthor: {
              id: currentUser.id,
              name: currentUser.username,
              email: currentUser.email,
              tel: currentUser.tel,
              title: currentUser.title,
            },
            changeType: ChangeEventType.UPDATED,
            changes: changes,
          };

          // Add the change event to the booking's change history
          newBooking.changes = [...(oldBooking.changes || []), changeEvent];
        }

        // Check if there are significant changes that should trigger a hash update
        const significantChanges = { ...changes };
        //        console.log('significantChanges', significantChanges);

        // Remove non-significant fields
        delete significantChanges.pdfUrl;
        if (significantChanges.voucher?.voucherUrl) {
          delete significantChanges.voucher;
        }

        //prune out tourConfirm-only guide toggles
        this.pruneTourConfirmChanges(
          significantChanges,
          oldBooking,
          newBooking,
        );

        // Now decide on hash
        if (Object.keys(significantChanges).length > 0) {
          newBooking.hash = uuidv4();
        } else {
          newBooking.hash = oldBooking?.hash ?? uuidv4();
        }
      } else {
        if (oldBooking.hash) {
          newBooking.hash = oldBooking.hash;
        }
        if (oldBooking.changes) {
          newBooking.changes = oldBooking.changes;
        }
      }
    }

    if (!newBooking.hash) {
      newBooking.hash = uuidv4();
    }

    // Always update the document regardless of whether changes were detected
    return setDoc(bookingDocRef, newBooking, { merge: false });
  }

  detectChanges(
    oldBooking?: Booking,
    newBooking?: Booking,
  ): Partial<Booking> | null {
    if (!oldBooking) return newBooking ?? null; // If no old data, return full newBooking
    if (!newBooking) return null; // No new data, no changes

    // Start with a basic deep equality check
    if (_.isEqual(oldBooking, newBooking)) return null; // No changes detected

    // Create a changes object by comparing each property
    const changes = _.reduce(
      newBooking,
      (result, value, key) => {
        // Skip pdfUrl and voucher when detecting changes for change tracking purposes
        if (
          key === 'pdfUrl' ||
          key === 'voucher' ||
          key === 'hash' ||
          key === 'changes'
        ) {
          // Only include these fields if they were previously undefined/null but now have values
          const oldValue = oldBooking[key as keyof Booking];
          if (
            (oldValue === undefined || oldValue === null) &&
            value !== undefined &&
            value !== null
          ) {
            (result as any)[key] = value;
          }
        }
        // For all other fields, do a regular equality check
        else if (!_.isEqual(value, oldBooking[key as keyof Booking])) {
          (result as any)[key] = value;
        }
        return result;
      },
      {} as Partial<Booking>,
    );

    return Object.keys(changes).length > 0 ? changes : null;
  }
  setDeleteFlagBooking(
    booking: Booking,
    calendarWeekDate: string | Date,
  ): Promise<void> {
    if (!booking.id) {
      return Promise.reject(new Error('Booking ID is required for update'));
    }

    booking.deleteFlag = true;

    return this.updateBookingChanges(booking);
  }

  deleteBooking(
    calendarWeekDate: string | Date,
    shipId: string,
    bookingId: string,
  ): Promise<void> {
    const calendarWeek = getCalendarWeek(calendarWeekDate);
    const bookingDocRef = doc(
      this.firestore,
      `bookings/${calendarWeek}/ships/${shipId}/sub-bookings/${bookingId}`,
    );

    return deleteDoc(bookingDocRef);
  }

  /**
   * Remove any guide-level changes that differ only by tourConfirm toggles.
   * Mutates the `changes` object in-place.
   */
  private pruneTourConfirmChanges(
    changes: Partial<Booking>,
    oldBooking: Booking,
    newBooking: Booking,
  ): void {
    const guidesChanges = changes.guides;
    if (!guidesChanges) return;

    const prunedGuides: Record<string, newGuideInterface> = {};

    for (const [guideId, change] of Object.entries(guidesChanges)) {
      const oldGuide = oldBooking.guides?.[guideId];
      const newGuide = newBooking.guides?.[guideId];

      if (oldGuide && newGuide) {
        // 1) Pull tour off the guide, and keep all other fields separately
        const { tour: oldTour, ...oldRestFields } = oldGuide;
        const { tour: newTour, ...newRestFields } = newGuide;

        // 2) Strip out tourConfirm with distinct names
        const { tourConfirm: oldTourConfirm, ...oldTourSansConfirm } = oldTour;
        const { tourConfirm: newTourConfirm, ...newTourSansConfirm } = newTour;

        // 3) If anything besides tourConfirm changed, keep this guide
        if (
          !_.isEqual(oldRestFields, newRestFields) ||
          !_.isEqual(oldTourSansConfirm, newTourSansConfirm)
        ) {
          prunedGuides[guideId] = change;
        }
      } else {
        // guide added or removed → definitely significant
        prunedGuides[guideId] = change;
      }
    }

    if (Object.keys(prunedGuides).length > 0) {
      changes.guides = prunedGuides;
    } else {
      delete changes.guides;
    }
  }

  getSubBookingsForRange(range: TimestampRange): Observable<Booking[]> {
    const subBookingsCollection = collectionGroup(
      this.firestore,
      'sub-bookings',
    );

    const subBookingsQuery = query(
      subBookingsCollection,
      where('duration.date', '>=', range.start),
      where('duration.date', '<=', range.end),
      limit(5000),
    );

    return collectionData(subBookingsQuery, { idField: 'id' }) as Observable<
      Booking[]
    >;
  }

  getSubBookingsForRangeOnce(range: TimestampRange): Promise<Booking[]> {
    return firstValueFrom(this.getSubBookingsForRange(range));
  }

  // async getDataCount(): Promise<number> {
  //   const snapshot = await getCountFromServer(
  //     collectionGroup(this.firestore, 'sub-bookings'),
  //   );
  //   return snapshot.data().count;
  // }
  //
  // async estimateAverageDocSize(): Promise<number> {
  //   const q = query(collectionGroup(this.firestore, 'sub-bookings'), limit(20));
  //
  //   const snap = await getDocs(q);
  //
  //   let totalBytes = 0;
  //
  //   snap.forEach((doc) => {
  //     totalBytes += new Blob([JSON.stringify(doc.data())]).size;
  //   });
  //
  //   return totalBytes / snap.size;
  // }
  //
  // async estimateTotalSizeMB(): Promise<number> {
  //   const count = await this.getDataCount();
  //   const avgBytes = await this.estimateAverageDocSize();
  //
  //   const totalBytes = count * avgBytes;
  //   return totalBytes / 1024 / 1024;
  // }
  //
  //

  async updateGuideInFutureBookings(
    guide: GuideDetailInterface,
  ): Promise<number> {
    const now = Timestamp.now();
    const subBookings = collectionGroup(this.firestore, 'sub-bookings');

    const q = query(
      subBookings,
      where('guideIds', 'array-contains', guide.id),
      where('duration.date', '>=', now),
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) return 0;

    //Firestore Backlimit 500 -> WE CHUNK 500
    const chunks = _.chunk(snapshot.docs, 400);
    let updatedCount = 0;

    for (const chunk of chunks) {
      const batch = writeBatch(this.firestore);

      for (const bookingDoc of chunk) {
        // The document's own ref, not a path rebuilt from the date: bookings filed under the old
        // browser-local week key sit elsewhere, and one missing ref fails the whole batch.
        const ref = bookingDoc.ref;
        const booking = bookingDoc.data() as Booking;

        const guideEntry = booking.guides?.[guide.id];
        if (!guideEntry) continue;

        // Sprache mergen: bestehende isActive Werte erhalten
        // neue hinzufugen, gelöschte entfernen

        const oldLangs = guideEntry.guideDetails.languages || {};
        const newLangs: Record<string, boolean> = {};
        Object.keys(guide.languages).forEach((lang) => {
          newLangs[lang] = oldLangs[lang] ?? false;
        });

        const updatedGuide: newGuideInterface = {
          ...guideEntry,
          guideDetails: {
            ...guideEntry.guideDetails,
            forename: guide.forename,
            surname: guide.surname,
            tel: guide.tel,
            email: guide.email,
            languages: newLangs,
          },
        };

        batch.update(ref, {
          [`guides.${guide.id}`]: updatedGuide,
        });
        updatedCount++;
      }

      await batch.commit();
    }

    return updatedCount;
  }

  async migrateGuideIds(): Promise<void> {
    const subBookings = collectionGroup(this.firestore, 'sub-bookings');
    const snapshot = await getDocs(subBookings);

    console.log(`Gefundene Bookings: ${snapshot.size}`);

    const chunks = _.chunk(snapshot.docs, 400);
    let updatedCount = 0;

    for (const chunk of chunks) {
      const batch = writeBatch(this.firestore);

      for (const bookingDoc of chunk) {
        const b = bookingDoc.data() as Booking;
        const ids = b.guides ? Object.keys(b.guides) : [];
        console.log(`Booking ${bookingDoc.id} → guideIds: [${ids.join(', ')}]`);
        // Own ref instead of a rebuilt path, see updateGuideInFutureBookings.
        batch.update(bookingDoc.ref, { guideIds: ids });
        updatedCount++;
      }

      try {
        await batch.commit();
      } catch (e) {
        console.error('Batch commit failed:', e);
      }
    }

    console.log(`Migration abgeschlossen. Updated: ${updatedCount}`);
  }
}
